import React, { useEffect, useRef, useState, memo } from 'react';

const JournalEntry = memo(function JournalEntry({ 
  dateStr, 
  formattedDate, 
  note, 
  habits, 
  onNoteChange, 
  onToggleHabit 
}) {
  const textareaRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [localContent, setLocalContent] = useState(note?.content || '');

  // Debounce reference
  const debouncedUpdate = useRef(null);

  // Simplified textarea height adjustment
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Reset height first to get accurate scrollHeight
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  // Handle resize observation (from Note.js)
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    adjustTextareaHeight(); // Ensure height is set correctly when mounted

    const resizeObserver = new ResizeObserver(() => {
      adjustTextareaHeight();
    });

    resizeObserver.observe(textarea);
    return () => resizeObserver.disconnect();
  }, []);

  // Set initial height and adjust when content changes
  useEffect(() => {
    // Update local state when note content changes
    if (note?.content !== undefined) {
      setLocalContent(note.content || '');
    }
    
    // Use requestAnimationFrame to ensure DOM updates first
    requestAnimationFrame(() => {
      adjustTextareaHeight();
    });
  }, [note?.content]);

  // Update local content when note prop changes (if not focused)
  useEffect(() => {
    if (!isFocused && note?.content !== undefined) {
      setLocalContent(note.content || '');
    }
  }, [note?.content, isFocused]);

  const handleNoteChange = (e) => {
    const newValue = e.target.value;
    setLocalContent(newValue); // Update local state immediately
    
    // Adjust height immediately
    adjustTextareaHeight();
    
    // Clear any existing timeout
    if (debouncedUpdate.current) {
      clearTimeout(debouncedUpdate.current);
    }
    
    // Set a new timeout
    debouncedUpdate.current = setTimeout(() => {
      // Create a synthetic event object with the current value
      const syntheticEvent = {
        target: { value: newValue }
      };
      
      // Only call the parent handler after the debounce timeout
      onNoteChange(dateStr, syntheticEvent);
    }, 500);
  };

  // Clean up the timeout when component unmounts
  useEffect(() => {
    return () => {
      if (debouncedUpdate.current) {
        clearTimeout(debouncedUpdate.current);
      }
    };
  }, []);

  const handleFocus = () => {
    setIsFocused(true);
    
    // Handle DOM updates (simplified from Note.js)
    requestAnimationFrame(() => {
      adjustTextareaHeight();
      
      // Position cursor at end of text
      if (textareaRef.current) {
        const length = textareaRef.current.value.length;
        textareaRef.current.setSelectionRange(length, length);
      }
    });
  };

  return (
    <div className={`border-t p-6 border-gray-200 ${isFocused ? 'bg-gray-50' : ''}`}>
      <h2 className="text-xl font-bold tracking-wider">{formattedDate}</h2>
      
      <div className="flex pt-4 flex-wrap gap-1">
        {habits.map((habit, index) => (
          <React.Fragment key={habit.id}>
            <span
              onClick={() => onToggleHabit(habit.id, dateStr)}
              className={`cursor-pointer ${habit.entries?.[dateStr] ? 'text-blue-600 font-medium' : 'text-gray-400'}`}
            >
              {habit.name}
            </span>
            {index < habits.length - 1 && <span className="text-gray-400">•</span>}
          </React.Fragment>
        ))}
      </div>

      <div className="relative">
        <textarea
          ref={textareaRef}
          className="w-full pt-4 bg-transparent border-none focus:outline-none resize-none overflow-hidden min-h-[24px] font-normal journal-textarea"
          placeholder="Write your note..."
          value={localContent}
          onChange={handleNoteChange}
          onFocus={handleFocus}
          onBlur={() => setIsFocused(false)}
          style={{ height: 'auto' }}
          rows="1"
        />
      </div>

      
    </div>
  );
});

export default JournalEntry; 